import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Calendar,
  ArrowRight,
  Clock,
  User,
  Tag,
  Search,
  TrendingUp,
  Sparkles,
  BarChart3
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';

const BlogPage = () => {
  const featuredPost = {
    title: '10 Social Media Trends to Watch in 2026',
    excerpt: 'Stay ahead of the curve with these emerging trends that are reshaping how brands connect with audiences on social media.',
    author: 'Sarah Johnson',
    date: 'Mar 1, 2026',
    readTime: '8 min read',
    category: 'Trends',
    image: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=800&h=400&fit=crop'
  };

  const posts = [
    {
      title: 'How to Create a Content Calendar That Works',
      excerpt: 'Learn the secrets of planning and organizing your social media content for maximum engagement.',
      author: 'Mike Chen',
      date: 'Feb 28, 2026',
      readTime: '5 min read',
      category: 'Strategy',
      image: 'https://images.unsplash.com/photo-1506784365847-bbad939e9335?w=400&h=250&fit=crop'
    },
    {
      title: 'AI in Social Media: A Complete Guide',
      excerpt: 'Discover how AI is revolutionizing content creation, scheduling, and analytics.',
      author: 'Emma Wilson',
      date: 'Feb 25, 2026',
      readTime: '10 min read',
      category: 'AI & Tech',
      image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400&h=250&fit=crop'
    },
    {
      title: 'Mastering Instagram Reels: Tips & Tricks',
      excerpt: 'Everything you need to know to create viral Reels that grow your audience.',
      author: 'Alex Rivera',
      date: 'Feb 22, 2026',
      readTime: '6 min read',
      category: 'Instagram',
      image: 'https://images.unsplash.com/photo-1611162618071-b39a2ec055fb?w=400&h=250&fit=crop'
    },
    {
      title: 'Building a Brand Voice That Resonates',
      excerpt: 'How to develop and maintain a consistent brand voice across all social platforms.',
      author: 'Sarah Johnson',
      date: 'Feb 18, 2026',
      readTime: '7 min read',
      category: 'Branding',
      image: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=400&h=250&fit=crop'
    },
    {
      title: 'Analytics Deep Dive: Metrics That Matter',
      excerpt: 'Cut through the noise and focus on the KPIs that actually drive business results.',
      author: 'David Park',
      date: 'Feb 15, 2026',
      readTime: '9 min read',
      category: 'Analytics',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=250&fit=crop'
    },
    {
      title: 'The Ultimate Guide to LinkedIn Marketing',
      excerpt: 'Strategies for B2B success on the world\'s largest professional network.',
      author: 'Lisa Thompson',
      date: 'Feb 12, 2026',
      readTime: '8 min read',
      category: 'LinkedIn',
      image: 'https://images.unsplash.com/photo-1611944212129-29977ae1398c?w=400&h=250&fit=crop'
    }
  ];

  const categories = ['All', 'Strategy', 'AI & Tech', 'Instagram', 'LinkedIn', 'TikTok', 'Branding', 'Analytics', 'Trends'];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="border-b border-slate-100 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/stellisocial-icon.svg" alt="StelliSocial" className="w-8 h-8 rounded-lg" />
              <span className="font-bold text-xl text-slate-900" style={{fontFamily: "'Tenor Sans', Georgia, serif"}}>Stelli<span className="text-emerald-500">Social</span></span>
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/features" className="text-slate-600 hover:text-slate-900">Features</Link>
              <Link to="/pricing" className="text-slate-600 hover:text-slate-900">Pricing</Link>
              <Link to="/integrations" className="text-slate-600 hover:text-slate-900">Integrations</Link>
              <Link to="/blog" className="text-emerald-600 font-medium">Blog</Link>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost">Sign In</Button>
              </Link>
              <Link to="/register">
                <Button className="bg-emerald-500 hover:bg-emerald-600">Start Free Trial</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="bg-emerald-100 text-emerald-700 mb-4">Blog</Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Insights & Resources
            </h1>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Tips, strategies, and industry insights to level up your social media game.
            </p>
          </div>

          {/* Search */}
          <div className="max-w-xl mx-auto relative mb-8">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input 
              placeholder="Search articles..." 
              className="pl-12 h-12 rounded-full border-slate-200"
            />
          </div>

          {/* Categories */}
          <div className="flex flex-wrap justify-center gap-2">
            {categories.map(cat => (
              <Button 
                key={cat} 
                variant={cat === 'All' ? 'default' : 'outline'} 
                size="sm"
                className={cat === 'All' ? 'bg-emerald-500 hover:bg-emerald-600' : ''}
              >
                {cat}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Post */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Card className="overflow-hidden border-slate-100 hover:shadow-lg transition-shadow">
              <div className="grid md:grid-cols-2">
                <div className="h-64 md:h-auto bg-slate-100">
                  <img 
                    src={featuredPost.image} 
                    alt={featuredPost.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-8 flex flex-col justify-center">
                  <Badge className="w-fit bg-emerald-100 text-emerald-700 mb-4">{featuredPost.category}</Badge>
                  <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">{featuredPost.title}</h2>
                  <p className="text-slate-600 mb-6">{featuredPost.excerpt}</p>
                  <div className="flex items-center gap-4 text-sm text-slate-500 mb-6">
                    <span className="flex items-center gap-1">
                      <User className="w-4 h-4" /> {featuredPost.author}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" /> {featuredPost.readTime}
                    </span>
                    <span>{featuredPost.date}</span>
                  </div>
                  <Button className="w-fit bg-emerald-500 hover:bg-emerald-600">
                    Read Article <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </CardContent>
              </div>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Posts Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-8">Latest Articles</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post, i) => (
              <motion.div
                key={post.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="h-full overflow-hidden border-slate-100 hover:shadow-lg transition-shadow cursor-pointer">
                  <div className="h-48 bg-slate-100">
                    <img 
                      src={post.image} 
                      alt={post.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <CardContent className="p-6">
                    <Badge variant="secondary" className="mb-3">{post.category}</Badge>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2 line-clamp-2">{post.title}</h3>
                    <p className="text-slate-600 text-sm mb-4 line-clamp-2">{post.excerpt}</p>
                    <div className="flex items-center justify-between text-sm text-slate-500">
                      <span>{post.author}</span>
                      <span>{post.readTime}</span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Button variant="outline" size="lg">Load More Articles</Button>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-20 bg-emerald-500">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Stay in the Loop</h2>
          <p className="text-emerald-100 mb-8">Get the latest social media tips and insights delivered to your inbox.</p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <Input placeholder="Enter your email" className="bg-white" />
            <Button className="bg-slate-900 hover:bg-slate-800">Subscribe</Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src="/stellisocial-icon-square-transparent.svg" alt="StelliSocial" className="w-8 h-8 rounded-lg" />
                <span className="font-bold  text-white" style={{fontFamily: "'Tenor Sans', Georgia, serif"}}>Stelli<span className="text-emerald-400">Social</span></span>
              </div>
              <p className="text-sm">The complete social media management platform for teams.</p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/features" className="hover:text-white">Features</Link></li>
                <li><Link to="/pricing" className="hover:text-white">Pricing</Link></li>
                <li><Link to="/integrations" className="hover:text-white">Integrations</Link></li>
                <li><Link to="/developers" className="hover:text-white">API</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Resources</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/blog" className="hover:text-white">Blog</Link></li>
                <li><Link to="/help" className="hover:text-white">Help Center</Link></li>
                <li><Link to="/community" className="hover:text-white">Community</Link></li>
                <li><Link to="/webinars" className="hover:text-white">Webinars</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="hover:text-white">About</Link></li>
                <li><Link to="/careers" className="hover:text-white">Careers</Link></li>
                <li><Link to="/privacy" className="hover:text-white">Privacy</Link></li>
                <li><Link to="/terms" className="hover:text-white">Terms</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-sm text-center">
            &copy; {new Date().getFullYear()} StelliSocial. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BlogPage;
